/**
 * Auto-capture click tracking for the configurator.
 *
 * A single document-level, capture-phase listener emits a `ui_click` for EVERY button
 * click on the page while the configurator is mounted. The configurator is a full-bleed
 * standalone experience — including its modals, which portal to `<body>` — so "every
 * button on the page" is effectively "every configurator button". That is what makes
 * tracking automatic for any button anyone adds later: an explicit
 * `data-ga-id="cfg-<page>-<action>"` is preferred (clean, stable reporting), but an
 * untagged button still emits a derived event (and, in dev, logs a nudge to tag it).
 */
import { track } from './track';

/** Elements treated as "buttons" for auto-tracking. */
const BUTTON_SELECTOR = 'button, [role="button"], [data-ga-id]';

/** Compact, stable-ish slug from button text, for derived ids. */
function slug(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

/** Best-effort human label for a button (`aria-label` wins over text). */
function labelFor(el: HTMLElement): string {
  const aria = el.getAttribute('aria-label');
  if (aria) return aria.trim();
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Start auto-capture click tracking. Returns a disposer — mount/unmount it with the
 * configurator root so the listener lives exactly as long as the configurator does.
 */
export function attachConfiguratorClickTracking(): () => void {
  const onClick = (ev: MouseEvent): void => {
    const target = ev.target;
    if (!(target instanceof Element)) return;
    const el = target.closest<HTMLElement>(BUTTON_SELECTOR);
    if (!el) return;

    const explicit = el.dataset.gaId;
    const label = labelFor(el);
    const derived = explicit === undefined;
    const gaId = explicit ?? `cfg-auto-${slug(label) || 'unknown'}`;

    if (derived && import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- dev-only nudge to tag the button
      console.warn(
        `[analytics] configurator button without data-ga-id: "${label}". ` +
          'Add data-ga-id="cfg-<page>-<action>" for clean GA/PostHog reporting.',
      );
    }

    track({
      name: 'ui_click',
      gaId,
      derived,
      label: label || undefined,
      props: { path: window.location.pathname },
    });
  };

  document.addEventListener('click', onClick, { capture: true });
  return () => document.removeEventListener('click', onClick, { capture: true });
}
